import React from "react";
import i18n from "i18next";
import TableRenderer from "./components/TableRenderer";
import { normalizeTable, resolveOptions } from "./components/tableData";
require("./_datatable.scss");

const THEMES = ["basic", "striped", "rows-only", "solid", "thick"];

export function DataTable(base) {
  return {
    getConfig: function () {
      return {
        name: "DataTable",
        flavor: "react",
        displayName: i18n.t("DataTable.PluginName"),
        category: "text",
        // Edited in place on the canvas (double-click → TableRenderer); display
        // options live in the right-rail "Tablo" accordion. The old two-step
        // config modal (components/config-component.js) is no longer wired.
        needsConfigModal: false,
        needsConfirmation: false,
        needsTextEdition: false,
        icon: "view_stream",
        initialWidth: "100%",
        initialHeight: "auto",
        initialWidthSlide: "60%",
        initialHeightSlide: "auto",
        createFromLibrary: ["csv,application/vnd.ms-excel", "url"],
      };
    },
    getToolbar: function (state) {
      const opts = resolveOptions(state);
      const { keys } = normalizeTable(state && state.data, state && state.keys);
      return {
        main: {
          __name: "Main",
          accordions: {
            // Accordion keys render sorted; "basic" lands above "style". Not a
            // reserved accordion name, so its buttons write flat state keys
            // (read back through resolveOptions).
            basic: {
              __name: i18n.t("DataTable.table_settings.title"),
              icon: "table_chart",
              order: [
                "showSearch",
                "showPagination",
                "showRowChoice",
                "pageLength",
                "sortColumn",
                "sortOrder",
                "theme",
                "transparentBg",
              ],
              buttons: {
                showSearch: {
                  __name: i18n.t("DataTable.table_settings.show_search"),
                  type: "checkbox",
                  checked: !opts.disableFilter,
                },
                showPagination: {
                  __name: i18n.t("DataTable.table_settings.show_pagination"),
                  type: "checkbox",
                  checked: !opts.disablePagination,
                },
                showRowChoice: {
                  __name: i18n.t("DataTable.table_settings.show_row_choice"),
                  type: "checkbox",
                  checked: !opts.disableRowChoice,
                  hide: opts.disablePagination,
                },
                pageLength: {
                  __name: i18n.t("DataTable.table_settings.page_length"),
                  type: "number",
                  value: opts.initialPageLength,
                  min: 1,
                  max: 100,
                  hide: opts.disablePagination,
                },
                sortColumn: {
                  __name: i18n.t("DataTable.table_settings.sort_column"),
                  type: "select",
                  value: String(opts.sortColumn),
                  options: ["-1", ...keys.map((_, i) => String(i))],
                  labels: [
                    i18n.t("DataTable.table_settings.sort_none"),
                    ...keys.map(
                      (k, i) => k || i18n.t("DataTable.table_settings.column_n", { n: i + 1 })
                    ),
                  ],
                },
                sortOrder: {
                  __name: i18n.t("DataTable.table_settings.sort_order"),
                  type: "select",
                  value: opts.initialOrder,
                  options: ["ascending", "descending"],
                  labels: [
                    i18n.t("DataTable.options.ascending"),
                    i18n.t("DataTable.options.descending"),
                  ],
                  hide: opts.sortColumn === -1,
                },
                theme: {
                  __name: i18n.t("DataTable.theme.label"),
                  type: "select",
                  value: opts.theme,
                  options: THEMES,
                  labels: THEMES.map((t) => i18n.t("DataTable.theme." + t)),
                },
                transparentBg: {
                  __name: i18n.t("DataTable.table_settings.transparent_bg"),
                  type: "checkbox",
                  checked: opts.transparentBg,
                },
              },
            },
            style: {
              __name: i18n.t("DataTable.style"),
              icon: "palette",
              order: [
                "opacity",
                "padding",
                "borderWidth",
                "borderStyle",
                "borderColor",
                "borderRadius",
              ],
              buttons: {
                padding: {
                  __name: i18n.t("DataTable.padding"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 50,
                  units: "px",
                },
                borderWidth: {
                  __name: i18n.t("DataTable.border_width"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 10,
                  units: "px",
                },
                borderStyle: {
                  __name: i18n.t("DataTable.border_style"),
                  type: "select",
                  value: "solid",
                  options: [
                    "none",
                    "hidden",
                    "dotted",
                    "dashed",
                    "solid",
                    "double",
                    "groove",
                    "ridge",
                    "inset",
                    "outset",
                    "initial",
                    "inherit",
                  ],
                  labels: [
                    i18n.t("styles.border_style_options.none") || "none",
                    i18n.t("styles.border_style_options.hidden") || "hidden",
                    i18n.t("styles.border_style_options.dotted") || "dotted",
                    i18n.t("styles.border_style_options.dashed") || "dashed",
                    i18n.t("styles.border_style_options.solid") || "solid",
                    i18n.t("styles.border_style_options.double") || "double",
                    i18n.t("styles.border_style_options.groove") || "groove",
                    i18n.t("styles.border_style_options.ridge") || "ridge",
                    i18n.t("styles.border_style_options.inset") || "inset",
                    i18n.t("styles.border_style_options.outset") || "outset",
                    i18n.t("styles.border_style_options.initial") || "initial",
                    i18n.t("styles.border_style_options.inherit") || "inherit",
                  ],
                },
                borderColor: {
                  __name: i18n.t("DataTable.border_color"),
                  type: "color",
                  value: "#000000",
                },
                borderRadius: {
                  __name: i18n.t("DataTable.border_radius"),
                  type: "number",
                  value: 0,
                  min: 0,
                  max: 50,
                  step: 5,
                  units: "%",
                },
                opacity: {
                  __name: i18n.t("DataTable.opacity"),
                  type: "range",
                  value: 1,
                  min: 0,
                  max: 1,
                  step: 0.01,
                },
              },
            },
          },
        },
      };
    },
    getInitialState: function () {
      let data = [
        [
          [i18n.t("DataTable.options.examplename1")],
          [i18n.t("DataTable.options.exampleage1")],
          [i18n.t("DataTable.options.examplecountry1")],
        ],
        [
          [i18n.t("DataTable.options.examplename2")],
          [i18n.t("DataTable.options.exampleage2")],
          [i18n.t("DataTable.options.examplecountry2")],
        ],
        [
          [i18n.t("DataTable.options.examplename3")],
          [i18n.t("DataTable.options.exampleage3")],
          [i18n.t("DataTable.options.examplecountry3")],
        ],
        [
          [i18n.t("DataTable.options.examplename4")],
          [i18n.t("DataTable.options.exampleage4")],
          [i18n.t("DataTable.options.examplecountry4")],
        ],
        [
          [i18n.t("DataTable.options.examplename5")],
          [i18n.t("DataTable.options.exampleage5")],
          [i18n.t("DataTable.options.examplecountry5")],
        ],
        [
          [i18n.t("DataTable.options.examplename6")],
          [i18n.t("DataTable.options.exampleage6")],
          [i18n.t("DataTable.options.examplecountry6")],
        ],
      ];

      let keys = [
        [i18n.t("DataTable.options.example_name_surname_txt")],
        [i18n.t("DataTable.options.example_age_txt")],
        [i18n.t("DataTable.options.example_country_txt")],
      ];

      return {
        data: data,
        keys: keys,
        // One-shot: TableRenderer opens a freshly inserted table in edit mode.
        __autoEdit: true,
        // No initial sort — rows stay in the order the author typed them.
        sortColumn: -1,
        options: {
          disableFilter: false,
          disableRowChoice: false,
          disablePagination: false,
          pageSizeLabel: i18n.t("DataTable.options.pageSizeLabel_txt"),
          searchLabel: i18n.t("DataTable.options.searchLabel_txt"),
          searchPlaceholder: "",
          noDataLabel: i18n.t("DataTable.options.noDataLabel_txt"),
          initialPageLength: 5,
          initialSort: keys[0] || 0,
          initialOrder: "descending",
          theme: "solid",
        },
      };
    },
    getRenderTemplate: function (state, props) {
      return (
        <TableRenderer
          state={state}
          update={props && props.update}
          visor={!!(props && props.visor)}
        />
      );
    },
    fileChanged: function (event) {
      let files = event.target.files;
      let file = files[0];
      let reader = new FileReader();
      reader.onload = function () {
        base.setState("chartData", JSON.parse(this.result));
      };
      reader.readAsText(file);
    },
  };
}
/* eslint-enable react/prop-types */
